// Shared derivation logic for the Shelter housing-assignment UI (dwellings, facility/standalone
// rooms, and beds). Mirrors the guard logic already enforced server-side in global.php
// (getCaseHousingType/roomCurrentMode/getActiveCaseMemberCount) so the UI can show why a
// candidate can't be picked before the user attempts the action.

export interface HousingCandidate {
  case_id: number;
  name: string;
  size: number;
  housing_type: "bed" | "room" | "dwelling" | null;
  housing_id: number | null;
  housing_label: string | null;
}

export interface CandidateRow {
  id: any;
  name: string;
  sizeLabel?: string;
  selectable: boolean;
  note: string | null;
  noteClass: "text-muted" | "app-danger-text";
  candidate: any;
}

export type RoomMode = "vacant" | "family" | "individual";

// A room this large is dormitory-style, not a single family's room - matches the API's
// own guard in UserCase_RoomOccupantController.
export const MAX_FAMILY_ROOM_BEDS = 10;

export function describeHousingType(type: string): string {
  if (type === "bed") return "an individual bed";
  if (type === "room") return "a room";
  if (type === "dwelling") return "a dwelling";
  return type;
}

export function pluralizeCount(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export function pluralizePeople(n: number): string {
  return n === 1 ? "1 person" : `${n} people`;
}

// A room's mode is derived, never stored: an active bed_room_occupant means the whole room
// is assigned to one case ("family"); one or more of its beds carrying a case_id means beds
// are being assigned individually; otherwise it's vacant.
export function getRoomMode(room: { id: any; family_case_id: any }, beds: Array<{ room_id: any; case_id: any }>): RoomMode {
  if (room.family_case_id) return "family";
  const roomBeds = beds.filter((b) => b.room_id === room.id);
  if (roomBeds.some((b) => b.case_id)) return "individual";
  return "vacant";
}

// ---- shape of the inventory ----
// Every screen that shows housing re-derives the same three relationships. They live here so
// Settings, Shelter and the case housing flow all count a property the same way.

export function roomsOfProperty(rooms: any[], propertyId: any): any[] {
  return rooms.filter((r: any) => r.property_id === propertyId);
}

export function bedsOfRoom(beds: any[], roomId: any): any[] {
  return beds.filter((b: any) => b.room_id === roomId);
}

export function bedsOfProperty(beds: any[], rooms: any[], propertyId: any): any[] {
  const owned = roomsOfProperty(rooms, propertyId);
  return beds.filter((b: any) => owned.some((r: any) => r.id === b.room_id));
}

export interface PropertyCounts {
  roomCount: number;
  bedCount: number;
  occupiedBedCount: number;
  openBedCount: number;
}

// A room assigned to a whole case counts all of its beds as taken, since nobody else can use them.
export function propertyCounts(rooms: any[], beds: any[], propertyId: any): PropertyCounts {
  const owned = roomsOfProperty(rooms, propertyId);
  const ownedBeds = beds.filter((b: any) => owned.some((r: any) => r.id === b.room_id));
  const occupiedBedCount = ownedBeds.filter(
    (b: any) => !!b.occupant_id || !!owned.find((r: any) => r.id === b.room_id)?.family_case_id,
  ).length;
  return {
    roomCount: owned.length,
    bedCount: ownedBeds.length,
    occupiedBedCount,
    openBedCount: ownedBeds.length - occupiedBedCount,
  };
}

// Beds that can be handed out one at a time. A dwelling is assigned whole, so none of its rooms
// qualify, and a room assigned to a family has no free beds. Standalone and facility rooms qualify
// (a vacant one can still go either way). Bed Occupancy and Bed Inspection only cover these.
export function isIndividuallyAssignableRoom(room: { property_id?: any; family_case_id?: any }, facilities: any[]): boolean {
  if (room.family_case_id) return false;
  return !room.property_id || facilities.some((f: any) => f.id === room.property_id);
}

// Order for the room pickers on Bed Occupancy / Bed Inspection: each facility's rooms together (facilities
// A-Z), standalone rooms last, rooms A-Z within a group. Expects rooms carrying a `facility_name`.
export function sortRoomsForFilter(rooms: any[]): any[] {
  const byName = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
  return [...rooms].sort((a: any, b: any) => {
    if (!a.facility_name !== !b.facility_name) return a.facility_name ? -1 : 1;
    if (a.facility_name && b.facility_name && a.facility_name !== b.facility_name) return byName(a.facility_name, b.facility_name);
    return byName(a.name, b.name);
  });
}

export function roomDisplayName(room: any, facilities: any[]): string {
  const facility = room?.property_id ? facilities.find((f: any) => f.id === room.property_id) : undefined;
  return [facility?.name, room?.name].filter(Boolean).join(" · ");
}

export function bedDisplayName(bed: any, rooms: any[], facilities: any[]): string {
  const room = rooms.find((r: any) => r.id === bed?.room_id);
  const roomName = room ? roomDisplayName(room, facilities) : bed?.room_name;
  return [roomName, bed?.name].filter(Boolean).join(" · ");
}

interface PlaceLookup {
  rooms?: any[];
  facilities?: any[];
  beds?: any[];
}

// getCaseHousingDetails returns a bare name for a room or bed, which is ambiguous as soon as
// two facilities both have a "Room 3" - re-resolve it here, where the page already holds the
// room and facility lists. Falls back to the raw label when a caller has nothing to look in.
export function housingPlaceLabel(
  housing: { housing_type?: string | null; housing_id?: any; housing_label?: string | null },
  look: PlaceLookup,
): string {
  const raw = housing?.housing_label || "";
  if (housing?.housing_type === "room" && look.rooms && look.facilities) {
    const room = look.rooms.find((r: any) => r.id === housing.housing_id);
    return room ? roomDisplayName(room, look.facilities) : raw;
  }
  if (housing?.housing_type === "bed" && look.beds && look.rooms && look.facilities) {
    const bed = look.beds.find((b: any) => b.id === housing.housing_id);
    return bed ? bedDisplayName(bed, look.rooms, look.facilities) : raw;
  }
  return raw;
}

// joins only the address pieces that actually have a value, so a property with no address
// on file doesn't render as a trail of empty punctuation
export function propertyAddressLine(dwelling: any): string {
  const cityState = [dwelling?.city, dwelling?.state].filter(Boolean).join(", ");
  const location = [cityState, dwelling?.zip].filter(Boolean).join(" ");
  return [dwelling?.address_line_one, location].filter(Boolean).join(" ");
}

export interface PersonHousing {
  // "" when they have nowhere; a dwelling or room belongs to the whole case, a bed to them
  kind: "dwelling" | "room" | "bed" | "";
  place: string;
  label: string;
}

// Where one person sleeps. A dwelling or room assigned to the case covers everyone on it;
// otherwise it comes down to whether they hold a bed themselves.
export function personHousing(opts: {
  caseId: any;
  person: any;
  dwellings: any[];
  rooms: any[];
  facilities: any[];
  beds: any[];
}): PersonHousing {
  const caseIdNumber = Number(opts.caseId);

  const dwelling = opts.dwellings.find((d: any) => Number(d.occupant_case_id) === caseIdNumber);
  if (dwelling)
    return {
      kind: "dwelling",
      place: dwelling.name,
      label: `${dwelling.name} ${dwelling.dwelling_type_name ? `- ${dwelling.dwelling_type_name}` : ""}`,
    };

  const room = opts.rooms.find((r: any) => Number(r.family_case_id) === caseIdNumber);
  if (room) {
    const place = roomDisplayName(room, opts.facilities);
    return { kind: "room", place, label: `${place} Room` };
  }

  const bed = opts.beds.find((b: any) => b.occupant_type === opts.person?.type && b.occupant_id === opts.person?.id);
  if (bed) {
    const place = bedDisplayName(bed, opts.rooms, opts.facilities);
    return { kind: "bed", place, label: `${place} Bed` };
  }

  return { kind: "", place: "", label: "Not housed" };
}

// "Bed 10" must not sort before "Bed 2"
export function sortBedsByName(list: any[]): any[] {
  return list
    .slice()
    .sort((a: any, b: any) => (a?.name || "").localeCompare(b?.name || "", undefined, { numeric: true, sensitivity: "base" }));
}

export function candidatesByCaseId(candidates: HousingCandidate[]): Map<number, HousingCandidate> {
  const map = new Map<number, HousingCandidate>();
  candidates.forEach((c) => map.set(c.case_id, c));
  return map;
}

export interface NeedsHousingRow {
  caseId: number;
  caseName: string;
  size: number;
  needCount: number;
  // Where the rest of the case already sleeps, when some of them are placed: the room id to
  // point at, how many distinct rooms they're spread over, and the label - which only reads
  // as one place when they are in fact all in one place.
  placedRoomId: any | null;
  placedRoomCount: number;
  placedRoomLabel: string | null;
}

// Everyone marked residential who still has no bed of their own, grouped by their case.
//
// The residential flag is what intake sets to say a person is meant to be housed, and it
// stays set once they are - vacating someone never clears it. So "needs housing" is the
// gap between that standing designation and where they actually sleep; anyone not marked
// residential is on the case for other reasons and is not missing a bed.
//
// This also deliberately asks a person-level question, not a case-level one: the API reports
// a case as housed as soon as ONE of its people has a bed (getCaseHousingType stops at the
// first match), so a family of three with one person placed would otherwise disappear from
// view along with the two who are still out. A case assigned a whole room or dwelling is
// housed in one go and never appears here at all.
export function buildNeedsHousingRows(
  candidates: HousingCandidate[],
  possibleOccupants: any[],
  beds: any[],
  rooms: any[],
  facilities: any[],
): NeedsHousingRow[] {
  const bedByPerson = new Map<string, any>();
  beds.forEach((b) => {
    if (b.occupant_id) bedByPerson.set(`${b.occupant_type}-${b.occupant_id}`, b);
  });

  const roomLabel = (roomId: any) => {
    const room = rooms.find((r: any) => r.id === roomId);
    if (!room) return null;
    const facility = room.property_id ? facilities.find((f: any) => f.id === room.property_id) : undefined;
    return facility ? `${facility.name} · ${room.name}` : room.name;
  };

  return candidates
    .filter((c) => c.housing_type !== "room" && c.housing_type !== "dwelling")
    .map((c) => {
      const people = possibleOccupants.filter((p: any) => p.case_id === c.case_id);
      const need = people.filter((p: any) => p.is_residential === 1 && !bedByPerson.has(`${p.type}-${p.id}`));
      // A case split across rooms is rare enough that the first placed person decides where
      // the stragglers get pointed - but we count the rooms so the row can stop naming one
      // place when its people are in several.
      const placedRoomIds = people
        .map((p: any) => bedByPerson.get(`${p.type}-${p.id}`))
        .filter(Boolean)
        .map((b: any) => b.room_id)
        .filter((id: any, i: number, all: any[]) => all.indexOf(id) === i);

      return {
        caseId: c.case_id,
        caseName: c.name,
        size: c.size,
        needCount: need.length,
        placedRoomId: placedRoomIds.length > 0 ? placedRoomIds[0] : null,
        placedRoomCount: placedRoomIds.length,
        placedRoomLabel: placedRoomIds.length === 1 ? roomLabel(placedRoomIds[0]) : null,
      };
    })
    .filter((row) => row.needCount > 0)
    .sort((a, b) => a.caseName.localeCompare(b.caseName));
}

interface CaseCandidateOptions {
  targetKind: "room" | "dwelling";
  targetHousingId: any;
  capacity: number;
  search: string;
  // lets a case already in a dwelling say which one, with its address
  dwellings?: any[];
  // let a room or bed note name its facility instead of a bare "Room 3"
  rooms?: any[];
  facilities?: any[];
  beds?: any[];
}

// Ranks a candidate by what the user can actually do with it here, most actionable first:
//
//   0  nowhere yet and fits          - assign outright, nothing else has to happen
//   1  already in one of these and fits - a move this picker makes in a single click
//   2  housed some other way and fits - has to be vacated elsewhere before it can come here
//   3  too large                     - unusable here whatever its housing
//
// A case that does not fit sinks to the bottom regardless of its housing: it is not a
// near miss, it simply cannot go here. Within the two groups that can be acted on, bigger
// cases sort first, so the one that comes closest to filling the place leads and a single
// person does not take a dwelling that could hold a family.
//
// Returns [group, sizeSortKey] - group sorts first, sizeSortKey (only meaningful in groups
// 0 and 1) breaks ties by size descending.
function caseCandidateRank(c: HousingCandidate, targetKind: "room" | "dwelling", capacity: number): [number, number] {
  if (c.size > capacity) return [3, 0];
  if (!c.housing_type) return [0, -c.size];
  if (c.housing_type === targetKind) return [1, -c.size];
  return [2, 0];
}

// Candidate rows for assigning a whole case to a room or a dwelling. A case already
// housed a different way is blocked; one already housed the same way (a different room,
// or a different dwelling) is still selectable but flagged as a move; one too large for
// the target's bed count is blocked with a capacity note. Ordered by what can be acted on
// here rather than alphabetically - cases with nowhere to sleep first, then the ones this
// picker can move in one click (see caseCandidateRank).
export function buildCaseCandidateRows(candidates: HousingCandidate[], opts: CaseCandidateOptions): CandidateRow[] {
  const q = opts.search.trim().toLowerCase();
  return candidates
    .filter((c) => c.size > 0)
    .filter((c) => !q || c.name.toLowerCase().includes(q))
    .filter((c) => !(c.housing_type === opts.targetKind && c.housing_id === opts.targetHousingId))
    .sort((a, b) => {
      const [groupA, sizeKeyA] = caseCandidateRank(a, opts.targetKind, opts.capacity);
      const [groupB, sizeKeyB] = caseCandidateRank(b, opts.targetKind, opts.capacity);
      if (groupA !== groupB) return groupA - groupB;
      if (sizeKeyA !== sizeKeyB) return sizeKeyA - sizeKeyB;
      return a.name.localeCompare(b.name);
    })
    .map((c) => {
      let selectable = true;
      let note: string | null = null;
      let noteClass: "text-muted" | "app-danger-text" = "text-muted";

      if (c.housing_type && c.housing_type !== opts.targetKind) {
        selectable = false;
        note = `Already housed in ${describeHousingType(c.housing_type)} (${housingPlaceLabel(c, opts)}) — end that assignment first`;
        noteClass = "app-danger-text";
      } else if (c.housing_type === opts.targetKind) {
        // Same kind, different place: the API ends the old assignment as part of taking the
        // new one, so this is one supported action - offer it as a move rather than making
        // the user vacate first and leave the case with nowhere in between.
        // housing_id is a room id when targetKind is "room", and room and property ids are
        // separate sequences that collide, so only look for an address on a real dwelling.
        const current = c.housing_type === "dwelling" ? opts.dwellings?.find((d: any) => d.id === c.housing_id) : undefined;
        const address = current ? propertyAddressLine(current) : "";
        note = `Currently at ${housingPlaceLabel(c, opts)}${address ? ` · ${address}` : ""} — select to move them here`;
      }

      if (selectable && c.size > opts.capacity) {
        selectable = false;
        note = `Needs ${pluralizeCount(c.size, "bed")} — only ${opts.capacity} available here`;
        noteClass = "app-danger-text";
      }

      return {
        id: c.case_id,
        name: c.name,
        sizeLabel: pluralizePeople(c.size),
        selectable,
        note,
        noteClass,
        candidate: c,
      };
    });
}

interface PersonCandidateOptions {
  search: string;
  // let a "currently in" note name the actual bed, room and facility
  rooms?: any[];
  facilities?: any[];
  beds?: any[];
}

// Candidate rows for assigning one person to a bed. A person whose case is housed in a
// room or dwelling is blocked (that's a case-level assignment, not per-person); one whose
// case is already in a different bed is still selectable, flagged as a move. People not
// currently in a bed sort first (alphabetically), then people already in a bed elsewhere.
export function buildPersonCandidateRows(
  possibleOccupants: any[],
  housingByCaseId: Map<number, HousingCandidate>,
  opts: PersonCandidateOptions,
): CandidateRow[] {
  const q = opts.search.trim().toLowerCase();

  // housing_type is answered per CASE, not per person: getCaseHousingType reports "bed" as
  // soon as any one person on the case holds one. So it says nothing about whether THIS
  // person has a bed - only their own bed row does.
  const ownBedOf = (p: any) => opts.beds?.find((b: any) => b.occupant_type === p.type && b.occupant_id === p.id);

  // free to place, then people this would move out of another bed, then anyone whose case is
  // housed as a whole and so cannot be picked at all
  const rank = (p: any) => {
    const housing = housingByCaseId.get(p.case_id);
    if (!housing?.housing_type) return 0;
    // someone with no bed of their own is free to place, whatever their case-mates have
    if (housing.housing_type === "bed") return ownBedOf(p) ? 1 : 0;
    return 2;
  };

  const caseNameOf = (p: any) => housingByCaseId.get(p.case_id)?.name || "";

  return possibleOccupants
    .filter((p) => p.is_residential === 1)
    .filter((p) => !q || (p.name || "").toLowerCase().includes(q) || caseNameOf(p).toLowerCase().includes(q))
    .sort((a, b) => {
      const rankA = rank(a);
      const rankB = rank(b);
      if (rankA !== rankB) return rankA - rankB;
      return (a.name || "").localeCompare(b.name || "");
    })
    .map((p) => {
      const housing = housingByCaseId.get(p.case_id);
      let selectable = true;
      let note: string | null = null;
      let noteClass: "text-muted" | "app-danger-text" = "text-muted";

      if (housing && housing.housing_type && housing.housing_type !== "bed") {
        selectable = false;
        note = `Case housed in ${describeHousingType(housing.housing_type)} (${housingPlaceLabel(housing, opts)}) — end that assignment first`;
        noteClass = "app-danger-text";
      } else if (housing && housing.housing_type === "bed") {
        // Only note a move when this person is the one in a bed. The case reads as "bed"
        // whenever any of its people holds one, so a case-mate being placed used to put
        // "Currently in a bed" against someone who has nowhere to sleep.
        const ownBed = ownBedOf(p);
        if (ownBed) {
          const where = opts.rooms && opts.facilities ? bedDisplayName(ownBed, opts.rooms, opts.facilities) : "";
          note = where ? `Currently in ${where} — select to move them here` : `Currently in a bed — select to move them here`;
        }
      }

      return {
        // guardian and member ids come from separate tables and can collide numerically -
        // qualify by type so two different people never share a row key.
        id: `${p.type}-${p.id}`,
        name: p.name,
        sizeLabel: caseNameOf(p),
        selectable,
        note,
        noteClass,
        candidate: p,
      };
    });
}
